class NotificationSSEService {
  constructor() {
    this.abortController = null;
    this.listeners = [];
    this.retryAttempts = 0;
    this.maxRetries = 5;
    this.reconnectDelay = 1000;
    this.token = null;
    this.workspaceId = null;
    this.isConnected = false;
  }

  async connect(token, workspaceId) {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.isConnected && this.abortController && !this.abortController.signal.aborted) {
      return;
    }

    this.token = token;
    this.workspaceId = workspaceId;
    this.abortController = new AbortController();

    const baseURL = process.env.RAZZLE_RUNTIME_ALKYMETRICS_V2_URL || 'http://localhost:3000';
    const url = `${baseURL}/notification/me/stream?workspace=${workspaceId}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE request failed: ${response.status} ${response.statusText}`);
      }

      this.isConnected = true;
      this.retryAttempts = 0;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          this.processSSELine(line);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('SSE connection error:', error);
        this.isConnected = false;
        this.handleReconnection();
      }
    }
  }

  processSSELine(line) {
    if (line.startsWith('data: ')) {
      try {
        const data = line.substring(6);
        if (data.trim()) {
          const parsedData = JSON.parse(data);
          
          // Handle nested notification structure
          let notification = parsedData;
          if (parsedData.type === 'notification' && parsedData.notification) {
            notification = parsedData.notification;
          }
          
          // Additional validation
          if (notification && typeof notification === 'object') {
            this.notifyListeners(notification);
          } else {
            console.warn('Invalid notification data structure:', notification);
          }
        }
      } catch (error) {
        console.error('Error parsing SSE notification:', error, 'Line:', line);
      }
    }
  }

  handleReconnection() {
    if (this.retryAttempts < this.maxRetries && this.token && this.workspaceId) {
      this.retryAttempts += 1;
      setTimeout(() => {
        this.connect(this.token, this.workspaceId);
      }, this.reconnectDelay * this.retryAttempts);
    }
  }

  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  notifyListeners(notification) {
    this.listeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  disconnect() {
    if (typeof window !== 'undefined' && this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isConnected = false;
    this.retryAttempts = 0;
  }

  getConnectionStatus() {
    return this.isConnected;
  }
}

export const notificationSSEService = new NotificationSSEService();